/**
 * API Service - Axios Instance and Interceptors
 * 
 * Provides a configured axios instance for all API calls.
 * Handles JWT token attachment and error responses.
 * 
 * @validates Requirements 3.3 - JWT token handling
 */

import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { getToken, clearTokens } from './auth.service';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

/**
 * Get CSRF token from cookie
 */
function getCsrfTokenFromCookie(): string | null {
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === '_csrf' && value) {
      return decodeURIComponent(value);
    }
  }
  return null;
}

/**
 * Configured axios instance for API calls
 */
export const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Request interceptor - Attach JWT token
 */
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = getToken();
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Attach CSRF token for state-changing requests
    // Try cookie first, then localStorage as fallback
    const csrfToken = getCsrfTokenFromCookie() || localStorage.getItem('budgira_csrf');
    if (csrfToken && config.headers && ['post', 'put', 'delete', 'patch'].includes(config.method?.toLowerCase() || '')) {
      config.headers['X-CSRF-Token'] = csrfToken;
    }
    
    // If sending FormData, remove Content-Type header to let browser set it with boundary
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    
    return config;
  },
  (error) => Promise.reject(error)
);

/**
 * Response interceptor - Handle errors
 */
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    // Handle 401 Unauthorized - Token may be expired or invalid
    // Only clear tokens if a token was actually sent with the request
    // This prevents clearing tokens during initial page load race conditions
    if (error.response?.status === 401) {
      const requestHadToken = error.config?.headers?.Authorization;
      const requestUrl = error.config?.url || '';
      
      // Don't clear tokens for password verification endpoints
      // These return 401 for invalid password, not invalid token
      const isPasswordVerificationEndpoint = 
        requestUrl.includes('/email/change-request') ||
        requestUrl.includes('/password/change') ||
        requestUrl.includes('/user/email') ||
        requestUrl.includes('/user/password');
      
      // Don't redirect for login/register/auth check endpoints
      const isAuthEndpoint = 
        requestUrl.includes('/auth/login') ||
        requestUrl.includes('/auth/register') ||
        requestUrl.includes('/auth/me') ||
        requestUrl.includes('/auth/refresh');
      
      if (requestHadToken && !isPasswordVerificationEndpoint) {
        // Token was sent but rejected - clear it
        clearTokens();
        
        // Redirect to login page if not already on auth pages
        if (!isAuthEndpoint && !window.location.pathname.startsWith('/login') && !window.location.pathname.startsWith('/register')) {
          // Use window.location for a full page redirect to ensure clean state
          window.location.href = '/login?expired=true';
          return Promise.reject(error);
        }
      }
    }
    
    return Promise.reject(error);
  }
);

export default api;

// ============================================
// Interest calculation API functions
// ============================================

/**
 * Rate history entry for display purposes
 */
export interface RateHistoryEntry {
  rate: number;
  startDate: string;
  endDate: string | null;
}

export interface InterestCalculationResult {
  totalInterest: number;
  periodStart: string;
  periodEnd: string;
  paymentDate: string;
  method: string;
  annualRate?: number;
  includesSchedulers?: boolean;
  /** Rate history entries that apply to this calculation period */
  rateHistory?: RateHistoryEntry[];
  dailyDetails?: Array<{
    date: string;
    balance: number;
    dailyInterest: number;
  }>;
  biweeklyDetails?: Array<{
    periodStart: string;
    periodEnd: string;
    effectiveBalance: number;
    days: number;
    periodInterest: number;
  }>;
}

export interface InterestPendingAccount {
  id: string;
  name: string;
  bankName: string;
  interestRate: number;
  lastInterestCalculation: string | null;
  needsCalculation: boolean;
}

export interface CalculateInterestResponse {
  calculation: InterestCalculationResult | null;
  operationCreated: boolean;
  operationId?: string;
}

/**
 * Get interest estimate for an account (read-only, no operation created)
 * @param includeSchedulers - Include future scheduler occurrences in calculation (forecast mode)
 */
export async function getInterestEstimate(
  accountId: string, 
  includeSchedulers: boolean = false
): Promise<InterestCalculationResult | null> {
  const params = includeSchedulers ? '?includeSchedulers=true' : '';
  const response = await api.get<{ success: boolean; data: InterestCalculationResult | null }>(
    `/accounts/${accountId}/interest-estimate${params}`
  );
  return response.data.data;
}

/**
 * Calculate and optionally create/update interest estimate operation
 */
export async function calculateInterest(
  accountId: string,
  params: {
    createOperation?: boolean;
    categoryId?: string;
    counterpartyId?: string;
    paymentMethodId?: string;
  }
): Promise<CalculateInterestResponse> {
  const response = await api.post<{ success: boolean; data: CalculateInterestResponse }>(
    `/accounts/${accountId}/calculate-interest`,
    params
  );
  return response.data.data;
}

/**
 * Get accounts that need interest recalculation
 */
export async function getInterestPendingAccounts(): Promise<InterestPendingAccount[]> {
  const response = await api.get<{ success: boolean; data: InterestPendingAccount[] }>(
    '/accounts/interest-pending'
  );
  return response.data.data;
}
